import { useState } from 'react';
import { UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { useMutation, useQuery } from 'data/utils/useQuery';
import { QueryKeys, API_VERSION_V1, API_VERSION_V2, QueryEntityKeys } from 'data/constants';
import { PartyDto } from 'data/types';
import { useControllerService, useRetryRequestWithFailCount } from 'data/utils/hooks';
import { ApiError, LocalPartyControllerService, type LocalPartyListingDTO } from 'data/openapi';

const PARTIES_API_PATHS = {
  v1: {
    getContractByIdPath: 'v1/fetch',
    getPartiesPath: 'v1/parties',
    createPartyPath: 'v1/parties/allocate',
  },
  v2: {
    getContractByIdPath: '',
    getPartiesPath: 'v2/parties',
    createPartyPath: 'v2/parties',
    getContractsPath: 'v2/state/active-contracts',
  },
};

export const useContractById = (participantName: string, isRemote = false, apiVersion = API_VERSION_V1) => {
  return useMutation(async ({ contractId, templateId }: { contractId: string; templateId: string }) => {
    const res = await useControllerService(isRemote).postRequest(
      participantName,
      PARTIES_API_PATHS[apiVersion].getContractByIdPath,
      {
        contractId,
        templateId,
      },
    );
    return res.result;
  });
};

export const useContracts = (
  participantName: string,
  partyId: string,
  options?: UseQueryOptions<unknown, any, any>,
  isRemote = false,
  apiVersion = API_VERSION_V1,
) => {
  const baseKey = isRemote ? QueryKeys.REMOTE_PARTICIPANTS : QueryKeys.PARTICIPANTS;
  if (apiVersion === API_VERSION_V1) {
    return useQuery(
      [baseKey, partyId],
      async () => {
        const res = await useControllerService(isRemote).getRequest(participantName, `v1/query?readers=${partyId}`);
        return res;
      },
      options,
    );
  }
  return useQuery(
    [baseKey, partyId],
    async () => {
      const res = await useControllerService(isRemote).postRequest(
        participantName,
        PARTIES_API_PATHS[apiVersion].getContractsPath,
        {
          filter: {
            filtersByParty: {},
            filtersForAnyParty: {
              cumulative: [
                {
                  identifierFilter: {
                    WildcardFilter: {
                      value: {
                        includeCreatedEventBlob: true,
                      },
                    },
                  },
                },
              ],
            },
          },
          verbose: false,
          activeAtOffset: 20,
          eventFormat: null,
        },
      );
      console.log('useContracts: ', res);
      return res;
    },
    {
      ...options,
    },
  );
};

export type PartiesResponse = {
  parties: PartyDto[];
  nextPageToken: string;
};

export const useParties = (
  participantName: string,
  options?: UseQueryOptions<PartiesResponse, unknown, PartiesResponse>,
  isRemote = false,
  apiVersion = API_VERSION_V1,
  pageSize = 5,
  pageToken = '',
) => {
  const { retry } = useRetryRequestWithFailCount();
  const baseKey = isRemote ? QueryKeys.REMOTE_PARTICIPANTS : QueryKeys.PARTICIPANTS;

  return useQuery(
    [baseKey, participantName, 'parties', apiVersion, pageSize, pageToken],
    async () => {
      const res = await useControllerService(isRemote).getRequest(
        participantName,
        `${PARTIES_API_PATHS[apiVersion].getPartiesPath}${
          apiVersion === API_VERSION_V2 ? `?pageSize=${pageSize}&pageToken=${pageToken}` : ''
        }`,
      );

      if (apiVersion === API_VERSION_V1) {
        return { parties: res.result ?? [], nextPageToken: undefined };
      }

      return {
        parties: res.parties ?? res.partyDetails ?? [],
        nextPageToken: res.nextPageToken || undefined,
      };
    },
    {
      retry,
      ...options,
    },
  );
};

/** GET /participants/{name}/parties/local – the participant's own parties. */
export function useLocalParties(
  participantName: string,
  options?: UseQueryOptions<LocalPartyListingDTO, ApiError, LocalPartyListingDTO>,
) {
  return useQuery(
    [QueryKeys.PARTICIPANTS, participantName, QueryEntityKeys.LOCAL_PARTIES_ENTITY],
    () => LocalPartyControllerService.getLocalParties(participantName),
    {
      ...options,
      enabled: !!participantName && (options?.enabled ?? true),
    },
  );
}

// for API v2
export type BackgroundResult = {
  parties: PartyDto[];
  totalCount?: number;
  loadedCount?: number;
  isFetching?: boolean;
  isFinished?: boolean;
};

export type UsePartiesBackgroundOptions = UseQueryOptions<BackgroundResult, unknown, BackgroundResult> & {
  onProgress?: (loaded: number) => void; // callback per page fetch
};

export function usePartiesBackground(participantName: string, pageSize = 1000, options?: UsePartiesBackgroundOptions) {
  const [loadedCount, setLoadedCount] = useState(0);

  const { data, isFetched, isFetching, refetch } = useQuery(
    [QueryKeys.PARTICIPANTS, participantName, QueryEntityKeys.ALL_PARTIES_ENTITY, pageSize],
    async (): Promise<BackgroundResult> => {
      // reset when query starts
      setLoadedCount(0);

      async function fetchPage(token?: string, acc: PartyDto[] = []): Promise<PartyDto[]> {
        const res = await useControllerService(false).getRequest(
          participantName,
          `v2/parties?pageSize=${pageSize}&pageToken=${token ?? ''}`,
        );

        const batch: PartyDto[] = res?.parties ?? res?.partyDetails ?? [];
        const newAcc = [...acc, ...batch];

        // update loaded count
        setLoadedCount((prev) => {
          const newCount = prev + batch.length;
          options?.onProgress?.(newCount);
          return newCount;
        });

        if (res?.nextPageToken) {
          return fetchPage(res.nextPageToken, newAcc);
        }
        return newAcc;
      }

      const all = await fetchPage();

      // Local parties first
      const sorted = [...all.filter((p) => p.isLocal), ...all.filter((p) => !p.isLocal)];

      return {
        parties: sorted,
        totalCount: sorted.length,
        loadedCount: sorted.length,
        isFetching: false,
        isFinished: true,
      };
    },
    {
      staleTime: 5 * 60 * 1000, // cache 5 min
      refetchOnWindowFocus: false,
      ...options,
      select: (v) => ({
        ...v,
        loadedCount,
        isFetching: false,
        isFinished: true,
      }),
    },
  );

  return {
    ...data,
    loadedCount,
    isFinished: isFetched && !isFetching,
    isFetching,
    refetch,
  };
}

export const useCreateParty = (name: string, isRemote = false, apiVersion = API_VERSION_V1) => {
  const queryClient = useQueryClient();
  const baseKey = isRemote ? QueryKeys.REMOTE_PARTICIPANTS : QueryKeys.PARTICIPANTS;

  return useMutation<CreatePartyResponse, ApiError, PartyDto>(
    async (values: PartyDto) => {
      return useControllerService(isRemote).postRequest(
        name,
        PARTIES_API_PATHS[apiVersion].createPartyPath,
        values,
      ) as Promise<CreatePartyResponse>;
    },
    {
      onSuccess: () => {
        if (apiVersion === API_VERSION_V1) {
          queryClient.invalidateQueries([baseKey, name, QueryEntityKeys.PARTIES_ENTITY]);
        } else {
          queryClient.invalidateQueries([baseKey, name, QueryEntityKeys.ALL_PARTIES_ENTITY]);
        }
        queryClient.invalidateQueries([baseKey, name, QueryEntityKeys.LOCAL_PARTIES_ENTITY]);
      },
    },
  );
};

export type CreatePartyResponse = {
  partyDetails?: { party?: string };
  result?: { partyDetails?: { party?: string } };
};

export function getFullPartyIdFromCreateResponse(data: CreatePartyResponse | undefined): string {
  if (!data) return '';
  const party = data.partyDetails?.party ?? data.result?.partyDetails?.party;
  return party ?? '';
}
